import React, { useState, useEffect, useCallback, useRef } from 'react';

/**
 * RichTextEditor — a lightweight visual (WYSIWYG) editor so admins don't have to
 * type raw HTML. A toolbar inserts the HTML (headings, bold, lists, links, …)
 * into a contentEditable area; a toggle exposes the raw HTML for power users.
 * Emits HTML via onChange. No third-party dependency.
 */
function RichTextEditor({ value, onChange, labels = {} }) {
  const ref = useRef(null);
  const last = useRef(value || '');
  const [mode, setMode] = useState('visual');   // 'visual' | 'html'

  // Push the external value into the editable div only when it actually diverges
  // (open / mode switch / AI fill) — never on each keystroke, to keep the caret.
  useEffect(() => {
    if (mode === 'visual' && ref.current && value !== last.current) {
      ref.current.innerHTML = value || '';
      last.current = value || '';
    }
  }, [value, mode]);

  const sync = () => { if (ref.current) { const h = ref.current.innerHTML; last.current = h; onChange(h); } };
  const exec = (cmd, arg) => { document.execCommand(cmd, false, arg); sync(); ref.current && ref.current.focus(); };
  const link = () => { const u = window.prompt(labels.linkUrl || 'Link URL', 'https://'); if (u) exec('createLink', u); };

  const B = (label, on, title) => (
    <button type="button" title={title} onMouseDown={e => e.preventDefault()} onClick={on}
      style={{ padding:'.3rem .55rem', borderRadius:6, border:'1px solid rgba(255,255,255,.14)', background:'rgba(255,255,255,.06)', color:'#e9e6e1', cursor:'pointer', fontSize:'.8rem', fontWeight:700, lineHeight:1 }}>{label}</button>
  );
  const box = { minHeight:240, background:'rgba(0,0,0,.25)', border:'1px solid rgba(255,255,255,.14)', borderRadius:8, color:'#f5f0e8', padding:'.7rem .8rem', outline:'none', overflowY:'auto' };

  return (
    <div>
      <div style={{ display:'flex', gap:'.3rem', flexWrap:'wrap', marginBottom:'.4rem' }}>
        {B('B', () => exec('bold'), 'Bold')}
        {B('I', () => exec('italic'), 'Italic')}
        {B('H2', () => exec('formatBlock', 'h2'), 'Heading')}
        {B('H3', () => exec('formatBlock', 'h3'), 'Subheading')}
        {B('¶', () => exec('formatBlock', 'p'), 'Paragraph')}
        {B('• —', () => exec('insertUnorderedList'), 'Bulleted list')}
        {B('1. —', () => exec('insertOrderedList'), 'Numbered list')}
        {B('❝', () => exec('formatBlock', 'blockquote'), 'Quote')}
        {B('🔗', link, 'Insert link')}
        {B('⌫', () => exec('removeFormat'), 'Clear formatting')}
        <div style={{ flex:1 }} />
        {B(mode === 'visual' ? '</>' : '👁', () => setMode(m => m === 'visual' ? 'html' : 'visual'), mode === 'visual' ? (labels.html || 'HTML') : (labels.visual || 'Visual'))}
      </div>
      {mode === 'visual'
        ? <div ref={ref} contentEditable suppressContentEditableWarning dir="auto" onInput={sync}
            style={{ ...box, fontFamily:'inherit', fontSize:'.92rem', lineHeight:1.7 }} />
        : <textarea value={value} onChange={e => { last.current = e.target.value; onChange(e.target.value); }}
            style={{ ...box, width:'100%', fontFamily:'monospace', fontSize:'.85rem', lineHeight:1.6 }} />}
    </div>
  );
}

/**
 * BlogManager — list / write / generate / edit / delete blog posts for one scope
 * (platform or a single restaurant/store). Editing (or writing) a post saves the
 * primary language and the server re-translates it into every enabled language.
 *
 * Props:
 *   api      = { list(), generate(), create(fields), edit(slug, fields), remove(slug) }
 *              `create` is optional; the "Write a post" button only shows when given.
 *   accent   = brand color (string)
 *   t        = optional label map { generate, generating, write, writeTitle, edit,
 *              save, saving, publish, publishing, delete, confirmDelete, empty,
 *              title, excerpt, keywords, body, retranslateNote, translateNote, langs }
 */
export default function BlogManager({ api, accent = '#c8a96e', t = {} }) {
  const L = {
    generate: 'Generate a post now', generating: 'Generating…', empty: 'No posts yet.',
    write: 'Write a post', writeTitle: 'Write a new post',
    edit: 'Edit', save: 'Save & re-translate', saving: 'Saving…',
    publish: 'Publish & translate', publishing: 'Publishing…',
    delete: 'Delete', confirmDelete: 'Delete this post?', title: 'Title', excerpt: 'Excerpt',
    keywords: 'Keywords', body: 'Body', langs: 'Languages',
    htmlMode: 'Edit HTML', visualMode: 'Visual editor', linkUrl: 'Link URL',
    retranslateNote: 'Saving re-translates this post into every enabled language.',
    translateNote: 'Your post is translated into every enabled language automatically.',
    ...t,
  };
  const [posts, setPosts]   = useState(null);
  const [busy, setBusy]     = useState(false);
  // draft = { mode:'create'|'edit', slug?, primaryLang, fields }
  const [draft, setDraft]   = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.list().then(r => setPosts(r.data || [])).catch(() => setPosts([]));
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true);
    try { await api.generate(); load(); } catch (e) { alert(e.message || 'Failed'); }
    setBusy(false);
  };

  const openWrite = () => {
    const pl = (posts && posts[0] && posts[0].primaryLang) || 'en';
    setDraft({ mode: 'create', primaryLang: pl, fields: { title: '', excerpt: '', keywords: '', body: '' } });
  };

  const openEdit = (p) => {
    const pl = p.primaryLang || 'en';
    const f = (p.i18n && p.i18n[pl]) || {};
    setDraft({ mode: 'edit', slug: p.slug, primaryLang: pl, fields: { title: f.title || '', excerpt: f.excerpt || '', keywords: f.keywords || '', body: f.body || '' } });
  };

  const save = async () => {
    if (!draft.fields.title.trim() || !draft.fields.body.trim()) { alert(L.title + ' + ' + L.body); return; }
    setSaving(true);
    try {
      if (draft.mode === 'create') await api.create(draft.fields);
      else await api.edit(draft.slug, draft.fields);
      setDraft(null); load();
    } catch (e) { alert(e.message || 'Failed'); }
    setSaving(false);
  };

  const remove = async (slug) => {
    if (!window.confirm(L.confirmDelete)) return;
    try { await api.remove(slug); load(); } catch (e) { alert(e.message || 'Failed'); }
  };

  const fld = (k, v) => setDraft(d => ({ ...d, fields: { ...d.fields, [k]: v } }));
  const card = { background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.1)', borderRadius:12, padding:'1rem 1.2rem', marginBottom:'.75rem' };
  const input = { width:'100%', background:'rgba(0,0,0,.25)', border:'1px solid rgba(255,255,255,.14)', borderRadius:8, color:'#f5f0e8', padding:'.6rem .7rem', fontFamily:'inherit', fontSize:'.9rem', marginTop:'.25rem' };
  const btn = (bg) => ({ padding:'.5rem .9rem', borderRadius:8, border:'none', cursor:'pointer', fontWeight:700, fontSize:'.82rem', background:bg||'rgba(255,255,255,.1)', color: bg ? '#1a1a1a' : '#f5f0e8' });

  if (draft) {
    const creating = draft.mode === 'create';
    return (
      <div>
        <div style={{ marginBottom:'.75rem', display:'flex', gap:'.5rem', alignItems:'center' }}>
          <button style={btn()} onClick={() => setDraft(null)}>←</button>
          <strong style={{ color:'#fff' }}>{creating ? L.writeTitle : (draft.fields.title || draft.slug)}</strong>
          <span style={{ fontSize:'.72rem', color:'rgba(255,255,255,.4)' }}>({draft.primaryLang.toUpperCase()})</span>
        </div>
        <p style={{ fontSize:'.78rem', color:'rgba(255,255,255,.4)', marginBottom:'1rem' }}>{creating ? L.translateNote : L.retranslateNote}</p>
        <label style={{ fontSize:'.8rem', color:'rgba(255,255,255,.6)' }}>{L.title}
          <input style={input} value={draft.fields.title} onChange={e => fld('title', e.target.value)} /></label>
        <label style={{ fontSize:'.8rem', color:'rgba(255,255,255,.6)', display:'block', marginTop:'.75rem' }}>{L.excerpt}
          <input style={input} value={draft.fields.excerpt} onChange={e => fld('excerpt', e.target.value)} /></label>
        <label style={{ fontSize:'.8rem', color:'rgba(255,255,255,.6)', display:'block', marginTop:'.75rem' }}>{L.keywords}
          <input style={input} value={draft.fields.keywords} onChange={e => fld('keywords', e.target.value)} /></label>
        <div style={{ fontSize:'.8rem', color:'rgba(255,255,255,.6)', marginTop:'.75rem', marginBottom:'.35rem' }}>{L.body}</div>
        <RichTextEditor value={draft.fields.body} onChange={html => fld('body', html)} labels={{ html: L.htmlMode, visual: L.visualMode, linkUrl: L.linkUrl }} />
        <button style={{ ...btn(accent), marginTop:'1rem' }} disabled={saving} onClick={save}>
          {creating ? (saving ? L.publishing : '✍️ ' + L.publish) : (saving ? L.saving : '✨ ' + L.save)}
        </button>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display:'flex', gap:'.5rem', flexWrap:'wrap', marginBottom:'1rem' }}>
        {api.create && (
          <button style={btn(accent)} onClick={openWrite}>✍️ {L.write}</button>
        )}
        <button style={btn()} disabled={busy} onClick={generate}>
          {busy ? '⏳ ' + L.generating : '✨ ' + L.generate}
        </button>
      </div>
      {posts === null ? <p style={{ color:'rgba(255,255,255,.4)' }}>…</p>
        : posts.length === 0 ? <p style={{ color:'rgba(255,255,255,.4)' }}>{L.empty}</p>
        : posts.map(p => {
          const pl = p.primaryLang || 'en';
          const tt = (p.i18n && p.i18n[pl]) || {};
          const langs = (p.langs && p.langs.length ? p.langs : Object.keys(p.i18n || {}));
          return (
            <div key={p.slug} style={card}>
              <div style={{ display:'flex', justifyContent:'space-between', gap:'1rem', alignItems:'flex-start' }}>
                <div style={{ minWidth:0 }}>
                  <div style={{ color:'#fff', fontWeight:700, marginBottom:'.2rem' }}>{tt.title || p.slug}</div>
                  <div style={{ fontSize:'.78rem', color:'rgba(255,255,255,.4)' }}>
                    {new Date(p.publishedAt).toLocaleDateString()} · {L.langs}: {langs.map(c => c.toUpperCase()).join(', ')}
                    {p.source === 'manual' ? ' · ✍️' : ''}
                    {p.status === 'draft' ? ' · draft' : ''}
                  </div>
                </div>
                <div style={{ display:'flex', gap:'.4rem', flexShrink:0 }}>
                  <button style={btn()} onClick={() => openEdit(p)}>✏️ {L.edit}</button>
                  <button style={btn()} onClick={() => remove(p.slug)}>🗑</button>
                </div>
              </div>
            </div>
          );
        })}
    </div>
  );
}
